import { View, TouchableOpacity, StyleSheet } from 'react-native'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { setLocale } from '../store/slices/localeSlice'
import { selectAppLocale } from '../store/selectors/localeSelectors'
import type { AppLocale } from '../i18n/locale'
import { useAppTranslation } from '../i18n/useAppTranslation'
import { COLORS, RADIUS, SPACING } from '../constants/theme'
import UkFlagIcon from './localeFlags/UkFlagIcon'
import UaFlagIcon from './localeFlags/UaFlagIcon'

const FLAG_WIDTH = 28
const FLAG_HEIGHT = 20

const LOCALES: { id: AppLocale; Flag: typeof UkFlagIcon }[] = [
    { id: 'en', Flag: UkFlagIcon },
    { id: 'ua', Flag: UaFlagIcon },
]

interface LocaleSwitcherProps {
    compact?: boolean
}

const LocaleSwitcher = ({ compact = false }: LocaleSwitcherProps) => {
    const dispatch = useAppDispatch()
    const locale = useAppSelector(selectAppLocale)
    const { t } = useAppTranslation()

    const labelFor = (id: AppLocale) =>
        id === 'en' ? t('locale.switchToEnglish') : t('locale.switchToUkrainian')

    const flagWidth = compact ? 24 : FLAG_WIDTH
    const flagHeight = compact ? 17 : FLAG_HEIGHT

    return (
        <View style={[styles.row, compact && styles.rowCompact]}>
            {LOCALES.map(({ id, Flag }) => {
                const active = locale === id
                return (
                    <TouchableOpacity
                        key={id}
                        style={[styles.flagBtn, active && styles.flagBtnActive]}
                        onPress={() => dispatch(setLocale(id))}
                        accessibilityRole="button"
                        accessibilityLabel={labelFor(id)}
                        accessibilityState={{ selected: active }}
                    >
                        <View style={styles.flagWrap}>
                            <Flag width={flagWidth} height={flagHeight} />
                        </View>
                    </TouchableOpacity>
                )
            })}
        </View>
    )
}

const styles = StyleSheet.create({
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.xs,
    },
    rowCompact: {
        gap: 4,
    },
    flagBtn: {
        paddingHorizontal: SPACING.xs,
        paddingVertical: SPACING.xs,
        borderRadius: RADIUS.md,
        borderWidth: 2,
        borderColor: 'transparent',
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },
    flagBtnActive: {
        borderColor: COLORS.white,
        backgroundColor: 'rgba(255, 255, 255, 0.28)',
    },
    flagWrap: {
        borderRadius: 3,
        overflow: 'hidden',
    },
})

export default LocaleSwitcher
